import { AJUDA_CATALOGO } from "@/lib/ajuda/dialogos/catalogo";
import { useEffect, useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { GradeDeCategorias } from "@/components/comecar/selecao/GradeDeCategorias";

const semAcento = (t: string) => t.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/**
 * Procedimentos da base da plataforma pra clínica — sugeridos pelas especialidades dela, em cartões
 * por especialidade que abrem os procedimentos (o desenho do Começar). Os que a clínica já tem ficam
 * de fora. O que não existe na base cai no "Criar procedimento" (o cadastro de sempre).
 */
export function AdicionarProcedimentosDaBase({ open, onClose, onCriarNovo }: { open: boolean; onClose: () => void; onCriarNovo: () => void }) {
  const utils = trpc.useUtils();
  const lista = trpc.clinicaOnboarding.paraAdicionar.useQuery(undefined, { enabled: open });
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [busca, setBusca] = useState("");
  const [soSugeridos, setSoSugeridos] = useState(true);
  useEffect(() => {
    if (!open) return;
    setMarcados(new Set());
    setBusca("");
    setSoSugeridos(true);
  }, [open]);

  const adicionar = trpc.clinicaOnboarding.adicionar.useMutation({
    onSuccess: ({ criados, ligados }) => {
      utils.service.invalidate();
      utils.clinicaOnboarding.invalidate();
      toast.success(`${criados + ligados} procedimentos adicionados.`);
      onClose();
    },
    onError: (e) => toast.error(e.message || "Não foi possível adicionar."),
  });

  const itens = (lista.data ?? []).map((p) => ({
    id: p.id, nome: p.nome, grupo: p.grupo, detalhe: p.especialidades.join(", "), sugerido: p.sugerido, jaTem: p.jaTem,
  }));
  const temSugestao = itens.some((i) => i.sugerido && !i.jaTem);
  const termo = semAcento(busca.trim());
  const visiveis = itens.filter(
    (i) => !i.jaTem && (termo ? semAcento(`${i.nome} ${i.grupo} ${i.detalhe}`).includes(termo) : !soSugeridos || !temSugestao || i.sugerido),
  );
  const marcar = (ids: string[], sim: boolean) =>
    setMarcados((atual) => {
      const novo = new Set(atual);
      for (const id of ids) {
        if (sim) novo.add(id);
        else novo.delete(id);
      }
      return novo;
    });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 border border-zinc-200 bg-white p-0 shadow-xl sm:max-w-3xl dark:border-border dark:bg-card">
        <DialogHeader className="border-b border-zinc-100 px-5 py-4 text-left dark:border-border">
          <DialogTitle ajuda={AJUDA_CATALOGO.adicionarProcedimentos}>Adicionar procedimentos</DialogTitle>
          <DialogDescription>Da base da plataforma, sugeridos pelas especialidades da clínica. Preço você define depois.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 px-5 py-3 dark:border-border">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar procedimento ou especialidade..."
              className="h-9 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none dark:border-border dark:bg-card"
            />
          </div>
          {temSugestao && !termo && (
            <div className="inline-flex rounded-md border border-zinc-200 p-0.5 dark:border-border">
              {[true, false].map((v) => (
                <button
                  key={String(v)}
                  type="button"
                  onClick={() => setSoSugeridos(v)}
                  aria-pressed={soSugeridos === v}
                  className={cn("rounded px-2.5 py-1 text-xs font-medium", soSugeridos === v ? "bg-brand-50 text-brand-700 dark:bg-primary/15 dark:text-primary" : "text-zinc-600 dark:text-muted-foreground")}
                >
                  {v ? "Sugeridos" : "Todos"}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="min-h-[200px] flex-1 overflow-y-auto px-5 py-4">
          {lista.isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          ) : visiveis.length === 0 ? (
            <p className="py-12 text-center text-sm text-zinc-500 dark:text-muted-foreground">{termo ? "Nada encontrado na base." : "A clínica já tem todos os procedimentos sugeridos. Veja em Todos."}</p>
          ) : (
            <GradeDeCategorias
              itens={visiveis.map((i) => ({ id: i.id, nome: i.nome, categoria: i.grupo, marcado: marcados.has(i.id) }))}
              onAlterar={marcar}
              unidade="procedimentos"
              fechadas={!termo}
              compacta
            />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 border-t border-zinc-100 px-5 py-3 dark:border-border">
          <span className="text-xs text-zinc-500 dark:text-muted-foreground">Não achou?</span>
          <button
            type="button"
            onClick={() => {
              onClose();
              onCriarNovo();
            }}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 px-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            <Plus className="h-3.5 w-3.5" />
            Criar procedimento
          </button>
          <button
            type="button"
            disabled={marcados.size === 0 || adicionar.isPending}
            onClick={() => adicionar.mutate({ procedimentoIds: [...marcados] })}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {adicionar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {marcados.size ? `Adicionar ${marcados.size}` : "Adicionar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
